import { forwardRef } from 'react';
import PropTypes from 'prop-types';

/**
 * FileInput Component
 *
 * Hidden file input component with ref forwarding for programmatic triggering.
 * Typically used with custom UI triggers (buttons, drag-drop zones).
 *
 * @param {Object} props
 * @param {boolean} props.multiple - Allow multiple file selection
 * @param {string} props.accept - Accepted file types (e.g., "image/*", ".pdf,.doc")
 * @param {boolean} props.disabled - Whether the input is disabled
 * @param {Function} props.onChange - Callback when files are selected (receives File[])
 * @param {string} props.id - Input id attribute
 * @param {string} props.name - Input name attribute
 *
 * @example
 * const fileInputRef = useRef(null);
 *
 * <FileInput
 *   ref={fileInputRef}
 *   multiple
 *   accept="image/*"
 *   onChange={(files) => handleFiles(files)}
 * />
 * <button onClick={() => fileInputRef.current?.click()}>
 *   Select Files
 * </button>
 */
export const FileInput = forwardRef(function FileInput(
  {
    multiple = false,
    accept,
    disabled = false,
    onChange,
    id,
    name,
  },
  ref
) {
  const handleChange = (e) => {
    const files = Array.from(e.target.files || []);
    onChange?.(files);
  };

  return (
    <input
      ref={ref}
      type="file"
      multiple={multiple}
      accept={accept}
      onChange={handleChange}
      disabled={disabled}
      id={id}
      name={name}
      className="hidden"
    />
  );
});

FileInput.propTypes = {
  multiple: PropTypes.bool,
  accept: PropTypes.string,
  disabled: PropTypes.bool,
  onChange: PropTypes.func,
  id: PropTypes.string,
  name: PropTypes.string,
};
